import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { applyBrand, setTenantHint } from "@/lib/tenant";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = anonymous, object = authenticated user
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [brand, setBrand] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      // 8s timeout so a hanging backend never leaves the whole UI stuck on "Lädt…"
      const { data } = await api.get("/auth/me", { timeout: 8000 });
      setUser(data);
    } catch (e) {
      // Any error (401, 5xx, timeout, network) → treat as anonymous and let the router redirect to /login
      // eslint-disable-next-line no-console
      if (e?.code === "ECONNABORTED" || e?.message?.includes("timeout")) {
        console.warn("[auth] /me timed out; treating as anonymous");
      }
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from Emergent OAuth, skip /me check; AuthCallback handles it.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (user) setTenantHint(user.tenant);
    if (user === null) return;
    api.get("/tenant/info").then((r) => {
      applyBrand(r.data.brand_color);
      setBrand(r.data);
      if (r.data.portal_name) document.title = r.data.portal_name;
    }).catch(() => {});
  }, [user]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* noop */
    }
    setUser(false);
  };

  const value = { user, brand, loading, checkAuth, login, register, logout, formatApiErrorDetail, setUser };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
